/**
 * lib/socialpulseOidc.js — client-side OIDC "Continue with SocialPulse".
 *
 * Sister-app deployments (FoodPulse, IndianFamilyTree, TravelerPulse …)
 * kick off this flow when the user clicks the "Continue with SocialPulse"
 * button on Login.jsx. Fully browser-side — no backend involvement until
 * the callback page trades the code for a session.
 *
 * Env vars (frontend .env of the sister deployment):
 *   REACT_APP_SOCIALPULSE_OIDC_ISSUER  — e.g. https://socialpulse.com/api
 *   REACT_APP_SOCIALPULSE_OIDC_CLIENT_ID — e.g. foodpulse-web
 *
 * When either env var is empty we return null from `getSocialPulseSsoConfig`
 * so `<ContinueWithSocialPulse />` renders nothing — that's the correct
 * behaviour on SocialPulse itself.
 */

const ISSUER = (process.env.REACT_APP_SOCIALPULSE_OIDC_ISSUER || "").replace(/\/$/, "");
const CLIENT_ID = process.env.REACT_APP_SOCIALPULSE_OIDC_CLIENT_ID || "";
const STORAGE_KEY = "sp:oidc:pkce";

function isEnabled() {
  return !!(ISSUER && CLIENT_ID);
}

export function getSocialPulseSsoConfig() {
  if (!isEnabled()) return null;
  return { issuer: ISSUER, clientId: CLIENT_ID };
}

function b64u(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function randB64() {
  return b64u(crypto.getRandomValues(new Uint8Array(32)).buffer);
}

/**
 * Kick off the "Continue with SocialPulse" flow.
 * Generates PKCE + state + nonce, stores them in sessionStorage, then
 * redirects the browser to SocialPulse's authorize endpoint.
 */
export async function startSocialPulseLogin({ returnTo = "/" } = {}) {
  const cfg = getSocialPulseSsoConfig();
  if (!cfg) throw new Error("Continue with SocialPulse is not configured on this deployment.");

  const verifier = randB64();
  const state = randB64();
  const nonce = randB64();
  const challenge = b64u(
    await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))
  );

  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ verifier, state, nonce, returnTo }),
  );

  const params = new URLSearchParams({
    client_id: cfg.clientId,
    response_type: "code",
    redirect_uri: `${window.location.origin}/auth/socialpulse-callback`,
    scope: "openid profile email",
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  window.location.assign(`${cfg.issuer}/oauth/authorize?${params.toString()}`);
}

/**
 * Complete the flow after SocialPulse redirects back to
 * /auth/socialpulse-callback. Reads the code + state from the URL,
 * validates state, exchanges code for id_token, hands the id_token to
 * this sister app's backend, and returns { user, token, returnTo }.
 */
export async function finishSocialPulseLogin(apiBase) {
  const cfg = getSocialPulseSsoConfig();
  if (!cfg) throw new Error("Continue with SocialPulse is not configured on this deployment.");

  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const error = params.get("error");
  if (error) throw new Error(params.get("error_description") || error);
  if (!code || !state) throw new Error("Missing code or state on the callback URL.");

  const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
  if (!saved || saved.state !== state) throw new Error("state mismatch — possible CSRF attempt.");

  // 1. Exchange code for tokens at SocialPulse.
  const tokenRes = await fetch(`${cfg.issuer}/oauth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: cfg.clientId,
      code,
      redirect_uri: `${window.location.origin}/auth/socialpulse-callback`,
      code_verifier: saved.verifier,
    }),
  });
  if (!tokenRes.ok) throw new Error(`token exchange failed (${tokenRes.status})`);
  const tokens = await tokenRes.json();
  if (!tokens.id_token) throw new Error("No id_token in response");

  // 2. Hand the id_token to OUR backend so it can mint a local session
  //    cookie (shared JWT_SECRET across all sister apps).
  const localRes = await fetch(`${apiBase}/api/auth/oidc-session`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id_token: tokens.id_token }),
  });
  if (!localRes.ok) {
    const body = await localRes.text();
    throw new Error(`local session mint failed (${localRes.status}): ${body}`);
  }
  const session = await localRes.json();

  // 3. Clean up + return.
  sessionStorage.removeItem(STORAGE_KEY);
  return {
    user: session.user,
    token: session.token,
    returnTo: saved.returnTo || "/",
  };
}
